import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { FileText, Scale, AlertTriangle, Users, Globe, Ban } from 'lucide-react';

function FadeSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

export default function TermsOfService() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const sections = [
    { icon: <FileText size={22} />, title: '1. Acceptance of Terms', content: 'By accessing or using the CloudfiMusic website (cloudfimusic.com), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website. CloudfiMusic reserves the right to modify these terms at any time, and continued use constitutes acceptance of updated terms.' },
    { icon: <Users size={22} />, title: '2. Use of Website', content: 'You may use this website for lawful purposes only. You agree not to: reproduce, distribute, or modify any content without written permission; use automated tools to scrape or collect data; attempt to gain unauthorized access to our systems; impersonate any person or entity; upload malicious code or interfere with the website\u2019s functionality.' },
    { icon: <Scale size={22} />, title: '3. Intellectual Property', content: 'All content on this website \u2014 including text, images, logos, music, artwork, and design \u2014 is the property of CloudfiMusic / Xu\u00e2n Di\u1ec7u Media Co., Ltd or its licensors and is protected by copyright and intellectual property laws. Unauthorized use, reproduction, or distribution of any content is strictly prohibited.' },
    { icon: <Globe size={22} />, title: '4. Third-Party Links & Content', content: 'Our website may contain links to third-party services such as Spotify, Apple Music, and social media platforms. CloudfiMusic is not responsible for the content, privacy practices, or availability of these external services. Use of third-party services is subject to their respective terms and policies.' },
    { icon: <AlertTriangle size={22} />, title: '5. Limitation of Liability', content: 'CloudfiMusic provides this website and its content on an \u201cas is\u201d basis. We make no warranties, express or implied, regarding the accuracy, completeness, or reliability of the content. To the maximum extent permitted by law, CloudfiMusic shall not be liable for any indirect, incidental, or consequential damages arising from your use of the website.' },
    { icon: <Ban size={22} />, title: '6. Termination', content: 'We reserve the right to terminate or restrict your access to the website at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties. Upon termination, all provisions of these Terms that should survive will remain in effect.' },
  ];

  return (
    <div className="pp-page">
      <motion.div className="pp-hero" ref={heroRef}>
        <motion.div className="pp-hero-inner" style={{ y: heroY }}>
          <motion.div className="pp-hero-icon"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: 'spring', stiffness: 200 }}
          ><FileText size={48} /></motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>Terms of Service</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>Last updated: March 2025</motion.p>
        </motion.div>
      </motion.div>

      <div className="pp-content">
        {sections.map((s, i) => (
          <FadeSection key={i} delay={i * 0.08}>
            <div className="pp-card">
              <div className="pp-card-icon">{s.icon}</div>
              <h2>{s.title}</h2>
              <p>{s.content}</p>
            </div>
          </FadeSection>
        ))}

        <FadeSection delay={0.5}>
          <div className="pp-card">
            <div className="pp-card-icon"><Scale size={22} /></div>
            <h2>7. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of Vietnam. Any disputes shall be resolved in the competent courts of Vietnam. Contact: CloudfiMusic / Xu\u00e2n Di\u1ec7u Media Co., Ltd. Email: info@cloudfimusic.com. Company ID: 0402222404.</p>
          </div>
        </FadeSection>
      </div>

      <div className="pp-footer-nav">
        <Link to="/privacy-policy">&larr; Privacy Policy</Link>
        <Link to="/cookie-policy">Cookie Policy &rarr;</Link>
      </div>
    </div>
  );
}
